import { PageHero } from "@/components/PageHero";
import { SectionHeading } from "@/components/SectionHeading";
import { LeadForm } from "@/components/LeadForm";
import { CTASection } from "@/components/CTASection";
import { useConcierge } from "@/context/ConciergeContext";
import { NAP, IMAGES, FAQS } from "@/data/site";
import { Link } from "react-router-dom";
import { Phone, EnvelopeSimple, MapPin, Clock, Sparkle, ChatCircleDots } from "@phosphor-icons/react";
import { track } from "@/lib/api";
import { useSEO, localBusinessLd, breadcrumbLd, faqLd } from "@/lib/seo";

export default function Contact() {
  const { openConcierge } = useConcierge();

  useSEO({
    title: "Contact MRE Developments Calgary | Free Quote",
    description:
      "Book a free consultation with MRE Developments in Calgary. Call, email or send a quick request for construction, branding, web, marketing or AI. No surprises.",
    path: "/contact",
    image: IMAGES.commercial,
    jsonLd: [
      localBusinessLd(),
      faqLd(FAQS),
      breadcrumbLd([{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]),
    ],
  });

  return (
    <div data-testid="contact-page">
      <PageHero
        overline="Contact"
        title="Let's talk. It's free."
        intro="Tell us what you're building or growing. We'll book a free consultation and follow up within one business day — no jargon, no pressure, no surprises."
        breadcrumb={[{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]}
      />

      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16 lg:py-24 grid lg:grid-cols-12 gap-12">
        {/* Form */}
        <div className="lg:col-span-7">
          <SectionHeading overline="Free quote request" title="Start your project." />
          <div className="mt-8">
            <LeadForm defaultTrack="both" source="contact_page" />
          </div>
          <div className="mt-8 border border-border p-6 flex items-start gap-4 bg-secondary/30">
            <Sparkle size={24} weight="fill" className="text-primary shrink-0 mt-0.5" />
            <div>
              <h3 className="font-display uppercase tracking-tight text-xl">Want an instant ballpark?</h3>
              <p className="text-muted-foreground text-sm mt-1">
                Our AI concierge gives a quick estimate in under a minute.
              </p>
              <button
                onClick={() => {
                  track("cta_click", { cta: "open_concierge", location: "contact" });
                  openConcierge();
                }}
                data-testid="contact-open-concierge"
                className="mt-3 inline-flex items-center gap-2 font-mono-tech text-xs tracking-widest uppercase text-primary hover:gap-3 transition-all"
              >
                Launch concierge →
              </button>
            </div>
          </div>
          <div className="mt-4 border border-border p-6 flex items-start gap-4 bg-secondary/30">
            <ChatCircleDots size={24} weight="fill" className="text-primary shrink-0 mt-0.5" />
            <div>
              <h3 className="font-display uppercase tracking-tight text-xl">Just have a question?</h3>
              <p className="text-muted-foreground text-sm mt-1">
                Ask our assistant about services, timelines or service areas — any hour.
              </p>
              <Link
                to="/chat"
                onClick={() => track("cta_click", { cta: "open_chat", location: "contact" })}
                data-testid="contact-open-chat"
                className="mt-3 inline-flex items-center gap-2 font-mono-tech text-xs tracking-widest uppercase text-primary hover:gap-3 transition-all"
              >
                Ask MRE →
              </Link>
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="lg:col-span-5">
          <SectionHeading overline="Direct lines" title="Reach us now." />
          <div className="mt-8 border-t border-l border-border">
            {[
              { Icon: Phone, label: "Call", value: NAP.phoneDisplay, href: `tel:${NAP.phoneHref}`, testid: "contact-call" },
              { Icon: Phone, label: "Call (alt)", value: NAP.phoneDisplay2, href: `tel:${NAP.phoneHref2}`, testid: "contact-call-2" },
              { Icon: EnvelopeSimple, label: "Email", value: NAP.email, href: `mailto:${NAP.email}`, testid: "contact-email" },
              { Icon: MapPin, label: "Studio", value: `${NAP.street}, ${NAP.city}, ${NAP.region} ${NAP.postalCode}` },
              { Icon: Clock, label: "Hours", value: "Mon–Fri · 8:00–17:00 MT" },
            ].map((row) => (
              <div key={row.label} className="border-b border-r border-border p-6 flex items-start gap-4">
                <row.Icon size={22} className="text-primary shrink-0 mt-0.5" weight="light" />
                <div>
                  <p className="font-mono-tech text-[11px] tracking-widest uppercase text-muted-foreground">{row.label}</p>
                  {row.href ? (
                    <a href={row.href} onClick={() => row.testid?.includes("call") && track("click_to_call", { location: "contact" })} data-testid={row.testid} className="text-foreground hover:text-primary transition-colors">
                      {row.value}
                    </a>
                  ) : (
                    <p className="text-foreground">{row.value}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
          {/* Map */}
          <div className="mt-8 border border-border overflow-hidden" data-testid="contact-map">
            <iframe
              title="MRE Developments — Calgary service area map"
              src="https://www.openstreetmap.org/export/embed.html?bbox=-114.30%2C50.90%2C-113.85%2C51.18&layer=mapnik&marker=51.0664%2C-114.0628"
              className="w-full h-72 grayscale contrast-125"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      <CTASection title="Build it. Grow it. Together." subtitle="One partner for the structure and the signal — Calgary & area for builds, Canada-wide for digital." />
    </div>
  );
}
